import { KeyboardEvent, useState } from "react";
import { isValidEmail } from "../../helper/common";
import styles from "./GuarantorInviteSection.module.css";

export type GuarantorInviteMethod = "email" | "self";

export type GuarantorInvite = {
  name: string;
  email: string;
  guarantorMethod: GuarantorInviteMethod;
  relation: string;
};

type GuarantorInviteSectionProps = {
  currentUserEmail?: string;
  invites: GuarantorInvite[];
  onChange: (invites: GuarantorInvite[]) => void;
  _t: (key: string, context?: string) => string;
  variant?: "application" | "join";
  guarantorOptionalKey?: string;
};

const GuarantorInviteSection = ({
  currentUserEmail,
  invites,
  onChange,
  _t,
  variant = "application",
  guarantorOptionalKey,
}: GuarantorInviteSectionProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [method, setMethod] = useState<GuarantorInviteMethod>("email");
  const [relation, setRelation] = useState("");
  const [error, setError] = useState("");

  const resetForm = () => {
    setName("");
    setEmail("");
    setMethod("email");
    setRelation("");
    setError("");
    setIsOpen(false);
  };

  const addGuarantor = () => {
    const trimmedName = name.trim();
    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedName) {
      setError(_t("nameRequired"));
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      setError(_t("invalidEmail"));
      return;
    }
    if (!relation) {
      setError(
        _t("guarantorRelationRequired", "Guarantor relation is required")
      );
      return;
    }
    if (trimmedEmail === currentUserEmail?.toLowerCase()) {
      setError(_t("cannotAddYourself"));
      return;
    }
    if (invites.some((invite) => invite.email === trimmedEmail)) {
      setError(_t("emailAlreadyAdded"));
      return;
    }

    onChange([
      ...invites,
      {
        name: trimmedName,
        email: trimmedEmail,
        guarantorMethod: method,
        relation,
      },
    ]);
    resetForm();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      addGuarantor();
    }
  };

  return (
    <div
      className={`${styles.section} ${variant === "join" ? styles.joinVariant : ""
        }`}
    >
      {invites.map((invite) => (
        <div className={styles.addedPerson} key={invite.email}>
          <span className={styles.avatar}>{invite.name.charAt(0)}</span>
          <div className={styles.personDetails}>
            <strong>{invite.name}</strong>
            <p>
              {invite.email} · {_t("guarantor")}
            </p>
          </div>
          <button
            type="button"
            onClick={() =>
              onChange(invites.filter(({ email }) => email !== invite.email))
            }
            aria-label={_t("removeGuarantor")}
          >
            ×
          </button>
        </div>
      ))}

      {isOpen ? (
        <div
          className={`${styles.form} ${method === "self" ? styles.selfMethod : ""
            }`}
        >
          <div className={styles.formHeader}>
            <span>{_t("guarantor")}</span>
          </div>
          <input
            className={styles.nameInput}
            value={name}
            autoFocus
            onChange={(event) => {
              setName(event.target.value);
              setError("");
            }}
            onBeforeInput={(event) => {
              const data = (event.nativeEvent as InputEvent).data;
              if (data && !/^[A-Za-zÀ-ÿ\s'-]+$/.test(data)) {
                event.preventDefault();
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder={_t("fullName")}
            aria-label={_t("fullName")}
          />
          <input
            className={styles.emailInput}
            type="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value.toLowerCase());
              setError("");
            }}
            onKeyDown={handleKeyDown}
            placeholder="email@example.com"
            aria-label={_t("email")}
          />
          {method === "email" && (
            <select
              className={styles.relation}
              value={relation}
              onChange={(event) => {
                setRelation(event.target.value);
                setError("");
              }}
              aria-label={_t("guarantorRelation", "Guarantor relation")}
            >
              <option value="" disabled>
                {_t("guarantorRelation", "Guarantor relation")}
              </option>
              <option value="parent">{_t("parent")}</option>
              <option value="Family">{_t("Family")}</option>
              <option value="relative">{_t("relative")}</option>
              <option value="partner">{_t("partner")}</option>
              <option value="company">{_t("company")}</option>
              <option value="other">{_t("other")}</option>
            </select>
          )}
          <select
            className={styles.relation}
            value={relation}
            onChange={(event) => {
              setRelation(event.target.value);
              setError("");
            }}
            aria-label={_t("guarantorRelation", "Guarantor relation")}
          >
            <option value="" disabled>
              {_t("guarantorRelation", "Guarantor relation")}
            </option>
            <option value="parent">{_t("parent")}</option>
            <option value="Family">{_t("Family")}</option>
            <option value="relative">{_t("relative")}</option>
            <option value="partner">{_t("partner")}</option>
            <option value="company">{_t("company")}</option>
            <option value="other">{_t("other")}</option>
          </select>
          <select
            className={styles.method}
            value={method}
            onChange={(event) => {
              const nextMethod = event.target.value as GuarantorInviteMethod;
              setMethod(nextMethod);
              setError("");
            }}
            aria-label={_t("guarantorAddMethod")}
          >
            <option value="email">{_t("inviteByEmail")}</option>
            <option value="self">{_t("illFillItIn")}</option>
          </select>
          <button
            className={styles.addButton}
            type="button"
            onClick={addGuarantor}
            disabled={!name.trim() || !email.trim() || !relation}
          >
            {_t("add")}
          </button>
          <button
            className={styles.closeButton}
            type="button"
            onClick={resetForm}
            aria-label={_t("remove")}
          >
            <img src="/images/delete-icon.svg" alt="" />
          </button>
          {error && <p className={styles.error}>{error}</p>}
        </div>
      ) : (
        <button
          className={styles.openButton}
          type="button"
          onClick={() => setIsOpen(true)}
        >
          <span className={styles.addIcon}>+</span>
          <strong>{_t("addAGuarantor")}</strong>
          <em>· {_t(guarantorOptionalKey || "guarantorOptional")}</em>
        </button>
      )}
    </div>
  );
};

export default GuarantorInviteSection;
